/**
 * ArchitectureView — a fully-featured, drop-in architecture viewer.
 *
 * Wraps the same model pipeline + stress-layout animation loop that the
 * main app uses, so external embedders (marketing site, docs MDX,
 * Studio) get the EXACT same viewer experience the app shows. The
 * surface area is deliberately small: pass a `Vfs` (path → source), get
 * a fully interactive board.
 *
 * What's included:
 *   - useModel() pipeline (parse + resolve + serialize; widget CSS pump
 *     fires inside useModel and lands on the shared adopted stylesheet)
 *   - Stress-layout animation: prepareRootStressInput → stressLayoutSteps
 *     generator → publishes positions to a ref consumed by
 *     buildBoardFromModules each frame. Same FRAME_MS / dwell schedule
 *     as App.tsx. Off by default for static embeds; opt in with `animate`.
 *   - Drag / place handler: updates placements Map + pinnedIds + retains
 *     drag state so neighbours nudge instead of snapping.
 *   - Selection (selectedId + selectedEdgeId).
 *   - Ghost-mover highlight during ghost / ghostSettle solver phases.
 *   - flattenModulesWithParent() — required for nested children to
 *     resolve their parentId before the solver runs.
 *
 * What's NOT included (keeps the embed focused on diagram-rendering):
 *   sidebar, search modal, types mode, process mode toggle,
 *   blast-radius UI, file picker, navigation history. Embedders compose
 *   those at the page level if they want them.
 *
 * SSR-safety: this module imports only React + types at the top level.
 * All runtime work happens inside the function body (hooks don't fire
 * during Astro SSR with `client:visible`), so the file can be statically
 * analyzed without touching `window`/`HTMLElement`.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
} from "react";
import type { Vfs } from "@archlang/viewer-core";
import type { Viewport } from "@archlang/scene/board";
import { useModel, type ModelApi } from "../hooks/useModel.js";
import {
  useBoardLayout,
  type BoardLayoutController,
} from "../hooks/useBoardLayout.js";
import {
  InfiniteBoard,
  type InfiniteBoardHandle,
  type ItemDiagnostic,
  type PlacementOverrides,
} from "../board/index.js";
import type { ExportFocalOptions } from "../board/exportPng.js";
import type { HoverStore } from "../board/hoverStore.js";
import type { OverflowItem } from "../components/OverflowToolbar.js";
import { edgeFromKey, edgeToKey, type StableId, type ViewProjectionPlan } from "@archlang/scene/arch-model";
import type { EdgeDecoMap } from "../process/index.js";
import { ResizablePanel } from "../components/ResizablePanel.js";
import { WIDTH_TIERS } from "../components/widthTiers.js";
import { DetailsPanel } from "./DetailsPanel.js";
import type { AspectOverlay } from "../aspect-overlay.js";
import { useOperatorSignals } from "../board/cameraOperator.js";

// No aspect overlay in the embed; a shared empty value keeps `useBoardLayout`'s
// memo inputs reference-stable.
const EMPTY_OVERLAY: AspectOverlay = { modules: [], edges: [], parentByNodeId: new Map() };

// Stable empty default for the operator's editor-selection ids — a fresh `[]`
// per render would re-fire the selection effect every time.
const EMPTY_SELECTION_IDS: readonly string[] = [];

export interface ArchitectureViewProps {
  /** Virtual file system: path -> file content. Required unless `modelState` is
   *  passed (a host that already compiled the workspace — see `modelState`). */
  readonly sources?: Vfs;
  /**
   * A pre-compiled model to render instead of compiling `sources` here. Lets a
   * composition share ONE `useModel` across the board + nav (the workbench's
   * WorkspaceModelProvider) rather than compiling per-consumer. When set, the
   * internal `useModel` is fed `null` (a cheap no-op — no compiler is created).
   */
  readonly modelState?: ModelApi;
  /**
   * Package root to render, as a VFS path (e.g. "/services/api"). Defaults to
   * the repo root "/". In a monorepo the host sets this to the package that
   * owns the active file so the board shows that package, not the whole tree.
   */
  readonly rootPath?: string;
  /** Initial selection (optional). Used only when uncontrolled. */
  readonly initialSelectedId?: string | null;
  /** Controlled selected module id. When provided, the board reflects this
   *  instead of its internal state — set by a host (e.g. the workbench store)
   *  so the editor/sidebar and board stay in sync. */
  readonly selectedId?: string | null;
  /** Controlled selected edge id (paired with `selectedId`). */
  readonly selectedEdgeId?: string | null;
  /**
   * Blast-radius highlight: item ids to light up as the connected neighbourhood
   * of the current selection. A store-fed socket (the workbench's `blastRadius`
   * slice) — ArchitectureView only forwards it to the board; the host computes
   * which ids are in the radius. Empty/omitted = no highlight (the default for
   * read-only embeds with no blast-radius UI).
   */
  readonly highlightedIds?: readonly string[];
  /**
   * Per-item diagnostic badges (error/warning/TODO dots on nodes). A
   * host-computed socket — the workbench feeds the shared model's diagnostics
   * mapped to owning module ids (`buildDiagnosticsByModule`). Omitted = no
   * badges (read-only embeds with no diagnostics surface).
   */
  readonly diagnosticsByItem?: ReadonlyMap<string, readonly ItemDiagnostic[]>;
  /**
   * Process-playback step decorations. When a process is active the board
   * dims/highlights edges by their role in the current step. A host-fed socket
   * sourced from the workbench's `ProcessPlaybackProvider`; omitted = no process
   * overlay (the default — read-only embeds with no scrubber).
   */
  readonly activeProcessId?: string | null;
  readonly activeStepIndex?: number | null;
  readonly edgeDecoMap?: EdgeDecoMap;
  /** Acting node of the current self-referential step (ringed on the board). */
  readonly stepActorId?: string | null;
  /** Current step's endpoint node ids — drives step auto-framing. */
  readonly stepParticipants?: { readonly aId: string; readonly bId: string } | null;
  /**
   * Aspect / type member highlight: module ids (and interface paths) to outline
   * as members of the hovered/pinned aspect value or selected type. A host-fed
   * socket sourced from the workbench's aspect controller (`aspectHighlight`).
   * Distinct from `highlightedIds` (blast): this outlines membership, it does
   * not light the connected neighbourhood. Omitted = no member outline.
   */
  readonly memberHighlightIds?: readonly string[];
  readonly memberInterfacePaths?: readonly string[];
  /**
   * Aspect WEB overlay: synthetic aspect nodes + member→aspect edges folded
   * into the layout inputs so the board renders the "aspect web". A host-fed
   * socket sourced from the workbench's aspect controller (`aspectOverlay`,
   * built from the pinned web keys). Omitted = no overlay (the layout is the
   * plain model). Reference-stable empty default keeps the layout memo quiet.
   */
  readonly aspectOverlay?: AspectOverlay;
  /**
   * The active view's projection plan (show/hide/group/style). When set, the
   * board renders the PROJECTED view — the same plan `LayoutProvider` feeds the
   * workbench board — instead of the identity model. Omitted/null = the full
   * model (the standalone-embed + marketing default). This is what lets a
   * host-agnostic `ViewRenderer` render a board-representation view: it computes
   * the plan (via `useViewSurface`) and hands it here, so the board solves ONCE
   * with the projection applied (no LayoutProvider, no second solve).
   */
  readonly viewPlan?: ViewProjectionPlan | null;
  /**
   * Controlled hand-placement overrides. When provided the board is placement-
   * controlled — it renders these and reports drags via `onPlace`/`onUnpin`
   * instead of owning the map. The workbench injects the shared
   * PlacementsProvider service here (persisted + undo/redo). Omitted = the
   * board keeps ephemeral local placements (standalone embeds).
   */
  readonly placements?: PlacementOverrides;
  /** Controlled pinned-id set (paired with `placements`). Defaults to the keys
   *  of `placements` when omitted. */
  readonly pinnedIds?: ReadonlySet<string>;
  /** Drag committed a node to `bounds` (placement-controlled mode). */
  readonly onPlace?: (
    id: string,
    bounds: { x: number; y: number; w: number; h: number },
  ) => void;
  /** A node's override was released (placement-controlled mode). */
  readonly onUnpin?: (id: string) => void;
  /**
   * Shared board layout controller. When provided the board renders THIS
   * controller's `root`/drag machinery instead of solving locally — the
   * workbench injects the LayoutProvider service so the board + the details rail
   * read ONE layout. Omitted = the view solves its own layout (standalone
   * embeds). When provided, the internal `useBoardLayout` is fed `null` (a cheap
   * no-op — no engine is created).
   */
  readonly layout?: BoardLayoutController;
  /** Notify on selection change. */
  readonly onSelectChange?: (id: string | null) => void;
  /** Notify on edge-selection change. */
  readonly onSelectEdgeChange?: (edgeId: string | null) => void;
  /** Click-reveal (VIEWS §7): boundary-stub click → reveal the peer. */
  readonly onRevealBoundary?: (peerKey: string) => void;
  /** Shared board ↔ nav hover store (forwarded to the canvas). */
  readonly hoverStore?: HoverStore;
  /** Double-click "bring into view" fired with the item id (forwarded). */
  readonly onItemOpen?: (id: string) => void;
  /** Cmd/Ctrl-click an item → open it in a new focused board tab (forwarded). */
  readonly onItemOpenRooted?: (id: string, isSpace?: boolean) => void;
  /** Right-click an item → open a context menu at the pointer (forwarded). */
  readonly onItemContextMenu?: (id: string, x: number, y: number) => void;
  readonly onBackgroundContextMenu?: (e: ReactMouseEvent) => void;
  readonly onEdgeContextMenu?: (edgeId: string, x: number, y: number) => void;
  /** Click a diagnostic in an item's issue popover → open it in the editor at
   *  its exact source location (forwarded). */
  readonly onOpenDiagnostic?: (id: string, diag: ItemDiagnostic) => void;
  /**
   * Opt in to the stress-layout animation loop. Off by default —
   * embeds get a single solve at mount, which is the right behaviour
   * for static diagrams. Pass `animate` when the host wants the
   * settling motion (e.g. the main app shell).
   */
  readonly animate?: boolean;
  /** Drop in extra className for the wrapper. */
  readonly className?: string;
  /**
   * Handle wheel for pan/zoom (default true). Set false for embedded
   * previews so a plain scroll over the canvas scrolls the page instead
   * of panning; ⌘/ctrl-zoom still works.
   */
  readonly wheel?: boolean;
  /**
   * Render the details sidebar (module/edge inspector + space overview) to the
   * right of the board. Off by default — embeds opt in (e.g. Studio's editor
   * preview). The breadcrumb navbar already renders inside the board chrome.
   */
  readonly showSidebar?: boolean;
  /**
   * Make the camera operator ("smart view") available: the board gains a
   * follow-edits toggle and, when the user turns it on, gently follows edits
   * and selection. Off by default (read-only embeds stay still); the editable
   * workbench opts in. The on/off state itself is board-owned + persisted.
   */
  readonly operator?: boolean;
  /** Force smart-view permanently on with no toggle (showcase/demo) — forwarded
   *  to the board. Ignores the persisted preference. Default off. */
  readonly operatorForced?: boolean;
  /**
   * Module ids covered by the EDITOR's current text selection — the camera
   * operator frames these ("frame what I selected in code"). Supplied by the
   * workbench (BoardPane maps the selected line range → ids); empty/omitted for
   * embeds with no editor, so selection never moves the camera there.
   */
  readonly operatorSelectionIds?: readonly string[];
  /** Emitted when the editor selection clears — drives the peek drift-back.
   *  `graceful`: caret stayed inside the framed node (grace before returning)
   *  vs moved away (return immediately). */
  readonly operatorSelectionCleared?: {
    readonly nonce: number;
    readonly graceful: boolean;
  } | null;
  /** Board ids the editor caret currently sits inside (innermost first). Lets
   *  the camera operator hold a framed node while the caret is parked in it.
   *  Supplied by the workbench (BoardPane maps the caret line → its enclosing
   *  module chain); empty/omitted for embeds with no editor. */
  readonly operatorCaretIds?: readonly string[];
  /**
   * Stable key of the rendered space — used as the board's `resetKey`, so a
   * genuine space switch re-frames the new package (a content edit keeps the
   * same key and never moves the viewport). When `initialViewport` is set for
   * the new space, the board restores it over the fit (see useViewport's
   * resetKey effect). Omit to key on `rootPath`.
   */
  readonly viewportKey?: string | null;
  /** Viewport to restore at mount — the remembered per-space viewport for the
   *  initially-active space. Absent → the board fits to content. */
  readonly initialViewport?: Viewport;
  /** Notified when the viewport changes (host debounces + persists). */
  readonly onViewportChange?: (vp: Viewport) => void;
  /**
   * Phone layout. Flips the board to its mobile chrome (top header + minimap
   * placement). The host (the viewer's `useMobileLayout`) decides; embeds stay
   * desktop. Forwarded straight to the board.
   */
  readonly mobile?: boolean;
  /** Phone settings menu (render prop; gets `close` to dismiss on a pick) —
   *  hosts what the desktop HUD pills carry. Forwarded to the board. */
  readonly mobileMenu?: (close: () => void) => ReactNode;
  /** Phone header search button → host's ⌘F modal. Absent → no button. */
  readonly onMobileSearch?: () => void;
  /** Host content before the breadcrumb in the board chrome (the viewer's
   *  nav-panel toggle). Opaque to the board. */
  readonly chromeLeading?: ReactNode;
  /** Action buttons for the board top bar's trailing slot (forwarded). */
  readonly chromeTrailingItems?: readonly OverflowItem[];
  /** Dev layout-debug overlay (bounding boxes / spacing guides). Host-toggled;
   *  forwarded to the board. Default off. */
  readonly debugLayout?: boolean;
  /** DEV-only chips docked above the minimap (layout debug controls);
   *  forwarded to the board. */
  readonly minimapExtras?: ReactNode;
}

/** Imperative handle so a host can drive the viewport (e.g. fly to the node an
 *  editor caret lands on) and run focal captures (export menus). Delegates to
 *  the inner board. */
export interface ArchitectureViewHandle {
  flyTo(moduleId: string): void;
  /** Navigate INTO a space by id (focal becomes the space itself). */
  enterSpace(moduleId: string): void;
  /** Re-fit the current focal to the container's current size after a host-
   *  driven reshape (e.g. switching the editor/board split orientation). */
  reframe(): void;
  /** Frame a connection's two endpoint nodes together (scrubber locate). */
  framePair(aId: string, bId: string): void;
  getViewport(): Viewport | null;
  restoreViewport(vp: Viewport): void;
  exportFocalAsPng(opts?: ExportFocalOptions): Promise<void>;
  exportFocalAsSvg(opts?: ExportFocalOptions): Promise<void>;
  copyFocalToClipboard(opts?: ExportFocalOptions): Promise<void>;
}

const SIDEBAR_WIDTH_KEY = "archlang.detailsSidebar.width.v1";

export const ArchitectureView = forwardRef<
  ArchitectureViewHandle,
  ArchitectureViewProps
>(function ArchitectureView(props, ref) {
  const {
    sources,
    rootPath,
    initialSelectedId = null,
    onSelectChange,
    onSelectEdgeChange,
    animate = false,
    className,
    wheel = true,
    showSidebar = false,
    operator = false,
    operatorForced = false,
    operatorSelectionIds = EMPTY_SELECTION_IDS,
    operatorSelectionCleared = null,
    operatorCaretIds = EMPTY_SELECTION_IDS,
  } = props;

  // Use a host-provided model if given; otherwise compile `sources` here. When
  // `modelState` is set, the internal hook gets `null` (no compiler created), so
  // there's no duplicate compile.
  const internalModel = useModel(
    props.modelState ? null : (sources ?? null),
    rootPath,
  );
  const { model, edges, structural, loading, error } =
    props.modelState ?? internalModel;

  // Edge-derived 1-hop adjacency (board-key domain) for the scope dial's ring 2.
  // Built from the dependency edges — NOT the engine graph's `#id` index, which
  // is empty for un-annotated modules. Undirected: a neighbour either direction.
  const operatorNeighborsOf = useMemo(() => {
    const adj = new Map<string, Set<string>>();
    const link = (a: string, b: string): void => {
      let set = adj.get(a);
      if (!set) adj.set(a, (set = new Set()));
      set.add(b);
    };
    for (const e of edges) {
      const from = edgeFromKey(e);
      const to = edgeToKey(e);
      if (from === to) continue;
      link(from, to);
      link(to, from);
    }
    return (id: string): readonly string[] => {
      const set = adj.get(id);
      return set ? [...set] : [];
    };
  }, [edges]);

  // Selection -----------------------------------------------------------
  // Controlled when the host passes `selectedId`/`selectedEdgeId`; otherwise the
  // board owns its own selection via this internal state.
  const controlled =
    props.selectedId !== undefined || props.selectedEdgeId !== undefined;
  const [internalId, setInternalId] = useState<StableId | null>(
    initialSelectedId as StableId | null,
  );
  const [internalEdgeId, setInternalEdgeId] = useState<string | null>(null);
  const selectedId = controlled
    ? ((props.selectedId ?? null) as StableId | null)
    : internalId;
  const selectedEdgeId = controlled
    ? (props.selectedEdgeId ?? null)
    : internalEdgeId;

  const handleSelect = useCallback(
    (id: string | null) => {
      if (!controlled) {
        setInternalId(id as StableId | null);
        setInternalEdgeId(null);
      }
      onSelectChange?.(id);
      if (id !== null) onSelectEdgeChange?.(null);
    },
    [controlled, onSelectChange, onSelectEdgeChange],
  );

  const handleSelectEdge = useCallback(
    (edgeId: string | null) => {
      if (!controlled) {
        setInternalEdgeId(edgeId);
        setInternalId(null);
      }
      onSelectEdgeChange?.(edgeId);
      if (edgeId !== null) onSelectChange?.(null);
    },
    [controlled, onSelectChange, onSelectEdgeChange],
  );

  // Placements (drag/pin state) ----------------------------------------
  // Controlled when the host passes `placements` (the workbench injects the
  // shared PlacementsProvider service — persisted + undo/redo); otherwise the
  // board owns ephemeral local placements, which is the right default for
  // standalone embeds (marketing demo, Studio preview) with no service.
  const placementsControlled = props.placements !== undefined;
  const [localPlacements, setLocalPlacements] = useState<PlacementOverrides>(
    () => new Map(),
  );
  const placements = props.placements ?? localPlacements;
  const pinnedIds = useMemo(
    () => props.pinnedIds ?? new Set(placements.keys()),
    [props.pinnedIds, placements],
  );

  // Board layout — the SAME incremental engine + drag/settle/animation
  // machinery the main app uses. A host can inject a SHARED controller (the
  // workbench's LayoutProvider) via `props.layout`; otherwise the view solves
  // its own. When a controller is injected the local hook is fed `null` so no
  // second engine is created (mirrors the `modelState` no-op pattern).
  const localLayout = useBoardLayout({
    model: props.layout ? null : model,
    edges,
    aspectOverlay: props.aspectOverlay ?? EMPTY_OVERLAY,
    placements,
    structural,
    viewPlan: props.viewPlan ?? null,
  });
  const layout = props.layout ?? localLayout;
  const { root, ghostMoverId, onPlaced, animateRecompute, moduleFocalPath } =
    layout;

  // Smart view ("camera operator"): edit signal from the shared model, selection
  // signal from the EDITOR's text selection (NOT the board click — see
  // useOperatorSignals). Inert unless `operator` is on (and the user enables the
  // board toggle). Computed unconditionally to keep hook order stable.
  const { editSignal, selectionSignal } = useOperatorSignals(
    model,
    structural,
    operatorSelectionIds,
    operator || operatorForced,
    edges, // resolve a process edit → the region it spans
    rootPath, // workspace identity — a swap resets edit history (not an edit)
  );

  const { onPlace, onUnpin } = props;
  const handlePlace = useCallback(
    (id: string, bounds: { x: number; y: number; w: number; h: number }) => {
      if (placementsControlled) onPlace?.(id, bounds);
      else
        setLocalPlacements((prev) => {
          const next = new Map(prev);
          next.set(id, bounds);
          return next;
        });
      onPlaced(); // kicks the settle tail + drag budget inside the hook
    },
    [placementsControlled, onPlace, onPlaced],
  );

  const handleUnpin = useCallback(
    (id: string) => {
      if (placementsControlled) {
        onUnpin?.(id);
        return;
      }
      setLocalPlacements((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    },
    [placementsControlled, onUnpin],
  );

  const boardRef = useRef<InfiniteBoardHandle>(null);

  // Expose viewport control + focal capture to the host (editor caret →
  // fly the board; export menu → capture).
  const notReady = () => Promise.reject(new Error("Board isn't ready yet"));
  useImperativeHandle(
    ref,
    () => ({
      flyTo: (id: string) => boardRef.current?.flyTo(id),
      enterSpace: (id: string) => boardRef.current?.enterSpace(id),
      reframe: () => boardRef.current?.reframe(),
      framePair: (aId: string, bId: string) =>
        boardRef.current?.framePair(aId, bId),
      getViewport: () => boardRef.current?.getViewport() ?? null,
      restoreViewport: (vp: Viewport) => boardRef.current?.restoreViewport(vp),
      exportFocalAsPng: (opts) =>
        boardRef.current?.exportFocalAsPng(opts) ?? notReady(),
      exportFocalAsSvg: (opts) =>
        boardRef.current?.exportFocalAsSvg(opts) ?? notReady(),
      copyFocalToClipboard: (opts) =>
        boardRef.current?.copyFocalToClipboard(opts) ?? notReady(),
    }),
    [],
  );

  // Animation: opt-in via `animate`. Runs the same relaxation the app's
  // "Animate" button drives, once per model load.
  const animationStartedRef = useRef(false);
  useEffect(() => {
    if (!animate || !model || animationStartedRef.current) return;
    animationStartedRef.current = true;
    void animateRecompute();
    // Runs once on first model load; subsequent edits re-solve via the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, animate]);

  // The details inspector (DetailsPanel) is a separate, opt-in component that
  // derives its own indexes from the shared model — see the render below.

  // Placeholder states --------------------------------------------------
  const wrapperClass =
    "w-full h-full bg-slate-950 text-slate-100 relative" +
    (className ? " " + className : "");

  // Only fall back to a placeholder on the FIRST load (no model yet). A
  // recompile after an edit keeps the prior `model`/`root` (useModel spreads the
  // previous state while `loading`), so we keep the board mounted and re-render
  // in place. Unmounting it here would destroy the viewport store and snap the
  // view back to the overview on every keystroke — even a transient parse error
  // mid-typing would otherwise blow the board (and the zoom) away.
  if (loading && !model) {
    return (
      <div
        className={
          wrapperClass +
          " flex items-center justify-center text-slate-400 text-sm"
        }
      >
        Parsing…
      </div>
    );
  }

  if (error && !model) {
    return (
      <div
        className={
          wrapperClass +
          " flex items-center justify-center text-red-400 text-sm"
        }
      >
        Error: {error}
      </div>
    );
  }

  if (!model || !root || model.modules.length === 0) {
    return (
      <div
        className={
          wrapperClass +
          " flex items-center justify-center text-slate-400 text-sm"
        }
      >
        No modules to display.
      </div>
    );
  }

  const board = (
    <InfiniteBoard
      ref={boardRef}
      initialViewport={props.initialViewport}
      onViewportChange={props.onViewportChange}
      root={root}
      selectedId={selectedId}
      selectedEdgeId={selectedEdgeId}
      highlightedIds={props.highlightedIds}
      diagnosticsByItem={props.diagnosticsByItem}
      activeProcessId={props.activeProcessId}
      activeStepIndex={props.activeStepIndex}
      edgeDecoMap={props.edgeDecoMap}
      stepActorId={props.stepActorId}
      stepParticipants={props.stepParticipants}
      memberHighlightIds={props.memberHighlightIds}
      memberInterfacePaths={props.memberInterfacePaths}
      pinnedIds={pinnedIds}
      ghostMoverId={ghostMoverId}
      onSelect={handleSelect}
      onSelectEdge={handleSelectEdge}
      onRevealBoundary={props.onRevealBoundary}
      onPlace={handlePlace}
      onUnpin={handleUnpin}
      wheel={wheel}
      // Model-derived focal-path resolver: lets flyTo + the camera operator
      // reach nodes below the lazy board's solved preview depth.
      moduleFocalPath={moduleFocalPath}
      // Camera operator ("smart view"): availability + live signals. The board
      // owns the on/off toggle; signals are inert until the user enables it.
      operator={operator}
      operatorForced={operatorForced}
      operatorEditSignal={editSignal}
      operatorSelectionSignal={selectionSignal}
      operatorSelectionCleared={operatorSelectionCleared}
      operatorCaretIds={operatorCaretIds}
      operatorNeighborsOf={operatorNeighborsOf}
      hoverStore={props.hoverStore}
      onItemOpen={props.onItemOpen}
      onItemOpenRooted={props.onItemOpenRooted}
      onItemContextMenu={props.onItemContextMenu}
      onBackgroundContextMenu={props.onBackgroundContextMenu}
      onEdgeContextMenu={props.onEdgeContextMenu}
      onOpenDiagnostic={props.onOpenDiagnostic}
      // Phone chrome (host-fed): mobile layout + settings menu + header search +
      // the nav-panel toggle. Absent on desktop / embeds → board stays desktop.
      mobile={props.mobile}
      mobileMenu={props.mobileMenu}
      onMobileSearch={props.onMobileSearch}
      chromeLeading={props.chromeLeading}
      chromeTrailingItems={props.chromeTrailingItems}
      debugLayout={props.debugLayout}
      minimapExtras={props.minimapExtras}
      // Re-frame only on a genuine space/package switch — not on every content
      // edit. Editing the active workspace re-renders with a new model but the
      // same key, so the viewport stays exactly where the user left it.
      resetKey={props.viewportKey ?? rootPath}
    />
  );

  if (!showSidebar) {
    return <div className={wrapperClass}>{board}</div>;
  }

  return (
    <div
      className={
        "w-full h-full bg-slate-950 text-slate-100 flex min-h-0" +
        (className ? " " + className : "")
      }
    >
      <div className="relative min-w-0 flex-1">{board}</div>
      <ResizablePanel
        side="right"
        ariaLabel="Details"
        widthKey={SIDEBAR_WIDTH_KEY}
        collapseKey="archlang.detailsSidebar.collapsed.v1"
        defaultWidth={300}
        {...WIDTH_TIERS.rail}
        className="overflow-y-auto border-l border-slate-800 bg-slate-900"
      >
        <DetailsPanel
          model={model}
          root={root}
          selectedId={selectedId}
          selectedEdgeId={selectedEdgeId}
          onSelectModule={handleSelect}
          onSelectEdge={handleSelectEdge}
        />
      </ResizablePanel>
    </div>
  );
});
